import {
  agoraEmSaoPaulo,
  encontrarJanelaCaixaAtiva,
  parseHorarioFuncionamentoRaw,
} from "./caixaRules";

export interface StoreManualOverride {
  mode: "open" | "closed";
  untilTs: number | null;
  indefinite: boolean;
  setAtTs: number;
  /**
   * Abrir/Fechar + “Automático”: ao expirar `untilTs`
   * (próximo fechamento / próxima abertura da agenda), volta ao modo horário
   * sem pedir “iniciar vendas”.
   */
  resumeOnSchedule?: boolean;
}

interface DayHours {
  open: boolean;
  openTime: string;
  closeTime: string;
}

type BusinessHoursLike = Record<string, DayHours | undefined> | undefined | null;

/**
 * Override manual com prazo (30m, 2h, 4h). `indefinite: true` = modo Automático na UI — segue a agenda.
 */
export function getActiveFiniteManualOverride(
  payTypesRaw: unknown,
  now: Date = agoraEmSaoPaulo()
): StoreManualOverride | null {
  const payTypes = parsePayTypesObject(payTypesRaw);
  const manualOverride = payTypes?.store_manual_override as StoreManualOverride | undefined;
  if (!manualOverride || (manualOverride.mode !== "open" && manualOverride.mode !== "closed")) {
    return null;
  }
  if (manualOverride.indefinite === true) {
    return null;
  }
  const untilTs = Number(manualOverride.untilTs || 0);
  if (untilTs > 0 && untilTs <= now.getTime()) {
    return null;
  }
  return manualOverride;
}

/** Modo automático: sem override finito (inclui `indefinite` legado ou campo ausente). */
export function isAutomaticStoreMode(payTypesRaw: unknown): boolean {
  return getActiveFiniteManualOverride(payTypesRaw) === null;
}

/**
 * Normaliza pay_types / payment_methods para o formato legado do front:
 * `{ aceitas: string[], outro_texto?, order_types_public?, logo_public?, ... }`.
 *
 * O backend Postgres guarda `payment_methods` como jsonb array; a migração Supabase
 * embrulha o objeto legado em `[{ ... }]`, e o front antigo lia só `.aceitas` no objeto.
 */
export function parsePayTypesObject(raw: any): Record<string, any> {
  let payTypes: any = raw ?? {};
  if (typeof payTypes === "string") {
    try {
      payTypes = JSON.parse(payTypes);
    } catch {
      payTypes = {};
    }
  }
  if (Array.isArray(payTypes)) {
    if (payTypes.length === 0) return {};
    const first = payTypes[0];
    if (first && typeof first === "object" && !Array.isArray(first)) {
      return first as Record<string, any>;
    }
    if (payTypes.every((x) => typeof x === "string")) {
      return { aceitas: payTypes as string[] };
    }
    return {};
  }
  if (!payTypes || typeof payTypes !== "object") return {};
  return payTypes as Record<string, any>;
}

/** Aplica parsePayTypesObject em pay_types e payment_methods de um registro de cliente. */
export function normalizeCustomerPayTypes(cliente: Record<string, any> | null | undefined): void {
  if (!cliente || typeof cliente !== "object") return;
  const parsed = parsePayTypesObject(
    (cliente as any).pay_types ?? (cliente as any).payment_methods
  );
  (cliente as any).pay_types = parsed;
  if ((cliente as any).pay_types?.order_types_public) {
    (cliente as any).order_types_public = (cliente as any).pay_types.order_types_public;
  }
}

export function getEffectiveStoreOpen(
  businessHours: BusinessHoursLike,
  customerOpen: boolean | null | undefined,
  payTypesRaw: any,
  now: Date = agoraEmSaoPaulo(),
  options?: {
    useCustomerOpenFallback?: boolean;
    /**
     * Quando false (ex.: painel do lojista), não inferir aberto/fechado só pela agenda
     * se o flag `open` do cliente ainda não estiver disponível — evita “Aberta” antes do servidor.
     * Páginas públicas mantêm default true para preservar comportamento anterior.
     */
    useScheduleFallbackWhenOpenUnknown?: boolean;
  }
): boolean {
  const scheduleOpen = isWithinScheduleHours(businessHours, now);
  const finiteOverride = getActiveFiniteManualOverride(payTypesRaw, now);
  if (finiteOverride) {
    return finiteOverride.mode === "open";
  }

  if (isAutomaticStoreMode(payTypesRaw)) {
    return scheduleOpen;
  }

  const useCustomerOpenFallback = options?.useCustomerOpenFallback !== false;
  if (useCustomerOpenFallback && customerOpen !== undefined && customerOpen !== null) {
    return customerOpen === true;
  }
  const scheduleFallbackWhenUnknown = options?.useScheduleFallbackWhenOpenUnknown !== false;
  if (!scheduleFallbackWhenUnknown && (customerOpen === undefined || customerOpen === null)) {
    return false;
  }
  return scheduleOpen;
}

/** Horário de expediente da agenda (ignora override e flag `open` do cliente). */
export function isWithinScheduleHours(
  businessHours: BusinessHoursLike,
  now: Date = agoraEmSaoPaulo()
): boolean {
  if (!businessHours || Object.keys(businessHours).length === 0) return false;

  const dayMapping: { [key: number]: string } = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  };

  function parseTimeToMinutes(timeValue: string | undefined): number {
    const [h, m] = (timeValue || "00:00").split(":");
    const hh = Number(h);
    const mm = Number(m);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return 0;
    return hh * 60 + mm;
  }

  const currentDayKey = dayMapping[now.getDay()];
  const currentDayHours = businessHours[currentDayKey];
  let scheduleOpen = false;

  if (currentDayHours && currentDayHours.open) {
    const openMinutes = parseTimeToMinutes(currentDayHours.openTime);
    const closeMinutes = parseTimeToMinutes(currentDayHours.closeTime || "23:59");
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    scheduleOpen =
      closeMinutes < openMinutes
        ? nowMinutes >= openMinutes || nowMinutes <= closeMinutes
        : nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
  }

  if (!scheduleOpen) {
    const prev = new Date(now);
    prev.setDate(prev.getDate() - 1);
    const prevKey = dayMapping[prev.getDay()];
    const prevHours = businessHours[prevKey];
    if (prevHours && prevHours.open) {
      const openMinutes = parseTimeToMinutes(prevHours.openTime);
      const closeMinutes = parseTimeToMinutes(prevHours.closeTime || "23:59");
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      if (closeMinutes < openMinutes && nowMinutes <= closeMinutes) {
        scheduleOpen = true;
      }
    }
  }

  return scheduleOpen;
}

/**
 * Janela de caixa (±2h do expediente) — pedidos entram no mesmo caixa após o fechamento;
 * não altera o badge “loja aberta” (isso segue agenda + flag manual no modo automático).
 */
export function isWithinCaixaWindow(
  horarioFuncionamentoRaw: unknown,
  now: Date = agoraEmSaoPaulo()
): boolean {
  try {
    const horarios = parseHorarioFuncionamentoRaw(horarioFuncionamentoRaw);
    if (horarios.length === 0) return false;
    return encontrarJanelaCaixaAtiva(horarios, now) !== null;
  } catch {
    return false;
  }
}

/**
 * Pedido pode ser auto-aceito / entrar com controle AGORA?
 *
 * Alinhado ao badge “Aberta/Fechada”:
 *   1. Override manual finito (30m, 2h, 4h).
 *   2. Horário da agenda (inclui turno overnight, ex. 18h–01h).
 *
 * A janela de caixa (+2h) **não** libera auto-aceite — só amarra o caixa
 * na aprovação manual (`isWithinCaixaWindow` / manage-caixa). Loja “Fechada”
 * → `awaiting_approval` + follow-up store_closed.
 */
export function canSubmitOrderNow(
  businessHours: BusinessHoursLike,
  customerOpen: boolean | null | undefined,
  payTypesRaw: any,
  _horarioFuncionamentoRaw?: unknown,
  now: Date = agoraEmSaoPaulo()
): { allowed: boolean; reason: "manual_override" | "caixa_window" | "store_open" | "schedule" | "closed" } {
  const finiteOverride = getActiveFiniteManualOverride(payTypesRaw, now);
  if (finiteOverride) {
    return {
      allowed: finiteOverride.mode === "open",
      reason: "manual_override",
    };
  }

  if (isWithinScheduleHours(businessHours, now)) {
    return { allowed: true, reason: "schedule" };
  }

  if (!isAutomaticStoreMode(payTypesRaw)) {
    const fallback = getEffectiveStoreOpen(businessHours, customerOpen, payTypesRaw, now, {
      useCustomerOpenFallback: true,
    });
    if (fallback) {
      return { allowed: true, reason: customerOpen === true ? "store_open" : "schedule" };
    }
  }

  return { allowed: false, reason: "closed" };
}
